import React, { useState } from "react";
import {
  useGetVaultsQuery,
  useAddVaultMutation,
  useUpdateVaultMutation,
  useDeleteVaultMutation,
} from "../../../redux/api/VaultApi/vaultApi";
import { message } from "antd";
import { MdAdd, MdContentCopy, MdVisibility, MdVisibilityOff, MdDelete, MdEdit } from "react-icons/md";
import { FcPrivacy } from "react-icons/fc";
import { FaLock, FaServer, FaCodeBranch, FaFolder, FaArrowLeft } from "react-icons/fa";
import { Container, Row, Col, Card, Button, Form, InputGroup, Alert } from "react-bootstrap";
import DeleteModel from "../../../Components/DeleteModel";

const ProjectVault = () => {
  const { data: vaults = [], isLoading, refetch, error: fetchError } = useGetVaultsQuery();
  const [addVault, { isLoading: isAdding }] = useAddVaultMutation();
  const [updateVault, { isLoading: isUpdating }] = useUpdateVaultMutation();
  const [deleteVault] = useDeleteVaultMutation();

  const [isModalOpen, setIsModalOpen] = useState(false); // Used as "isFormPageOpen" conceptually
  const [currentVault, setCurrentVault] = useState(null);
  const [visibleSecrets, setVisibleSecrets] = useState({});
  const [selectedEnvs, setSelectedEnvs] = useState({});
  const [activeProject, setActiveProject] = useState(null);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [vaultToDelete, setVaultToDelete] = useState(null);
  const [errorMessage, setErrorMessage] = useState("");

  const [showRowDeleteModal, setShowRowDeleteModal] = useState(false);
  const [rowToDelete, setRowToDelete] = useState(null);

  const [editorMode, setEditorMode] = useState("table"); // "table" | "bulk"
  const [bulkText, setBulkText] = useState("");

  const [formData, setFormData] = useState({
    project_name: "",
    environment: "Production",
    notes: "",
    credentials: [],
  });

  const parseBulkCredentials = (text) => {
    const lines = text.split("\n");
    const creds = [];
    lines.forEach((line) => {
      const trimmed = line.trim();
      if (!trimmed || trimmed.startsWith("#")) return;
      const equalIdx = trimmed.indexOf("=");
      if (equalIdx !== -1) {
        const key = trimmed.substring(0, equalIdx).trim();
        let val = trimmed.substring(equalIdx + 1).trim();
        if (
          (val.startsWith('"') && val.endsWith('"')) ||
          (val.startsWith("'") && val.endsWith("'"))
        ) {
          val = val.substring(1, val.length - 1);
        }
        if (key) {
          creds.push({ key, value: val, is_secret: true });
        }
      }
    });
    return creds;
  };

  const handleBulkTextChange = (text) => {
    setBulkText(text);
    const parsed = parseBulkCredentials(text);
    setFormData(prev => ({ ...prev, credentials: parsed }));
  };

  const handleOpenModal = (vault = null) => {
    if (vault) {
      setCurrentVault(vault);
      const initialCredentials = vault.credentials ? vault.credentials.map(cred => ({ ...cred })) : [];
      setFormData({
        project_name: vault.project_name,
        environment: vault.environment,
        notes: vault.notes || "",
        credentials: initialCredentials,
      });
      const text = initialCredentials.map(c => `${c.key}=${c.value}`).join("\n");
      setBulkText(text);
    } else {
      setCurrentVault(null);
      setFormData({
        project_name: "",
        environment: "Production",
        notes: "",
        credentials: [{ key: "", value: "", is_secret: true }],
      });
      setBulkText("");
    }
    setEditorMode("table");
    setIsModalOpen(true);
  };

  const handleOpenModalForNewEnv = (projectName, env) => {
    setCurrentVault(null);
    setFormData({
      project_name: projectName,
      environment: env,
      notes: "",
      credentials: [{ key: "", value: "", is_secret: true }],
    });
    setBulkText("");
    setEditorMode("table");
    setIsModalOpen(true);
  };

  const handleCloseModal = () => {
    setIsModalOpen(false);
    setCurrentVault(null);
    setErrorMessage("");
  };

  const handleAddCredential = () => {
    const updated = [...formData.credentials, { key: "", value: "", is_secret: true }];
    setFormData({
      ...formData,
      credentials: updated,
    });
    const text = updated.map(c => `${c.key}=${c.value}`).join("\n");
    setBulkText(text);
  };

  const handleRemoveCredentialClick = (index) => {
    setRowToDelete(index);
    setShowRowDeleteModal(true);
  };

  const confirmRemoveCredential = () => {
    if (rowToDelete !== null) {
      const updated = [...formData.credentials];
      updated.splice(rowToDelete, 1);
      setFormData({ ...formData, credentials: updated });
      const text = updated.map(c => `${c.key}=${c.value}`).join("\n");
      setBulkText(text);
    }
    setShowRowDeleteModal(false);
    setRowToDelete(null);
  };

  const closeRowDeleteModal = () => {
    setShowRowDeleteModal(false);
    setRowToDelete(null);
  };

  const handleCredentialChange = (index, field, value) => {
    const updated = formData.credentials.map((cred, i) =>
      i === index ? { ...cred, [field]: value } : cred
    );
    setFormData({ ...formData, credentials: updated });
    const text = updated.map(c => `${c.key}=${c.value}`).join("\n");
    setBulkText(text);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setErrorMessage("");
    try {
      if (currentVault) {
        await updateVault({ id: currentVault.id, ...formData }).unwrap();
        message.success("Project vault updated securely.");
      } else {
        await addVault(formData).unwrap();
        message.success("New project vault created.");
      }
      refetch();
      handleCloseModal();
    } catch (err) {
      setErrorMessage(err?.data?.message || err?.message || "Failed to save vault securely.");
      message.error("Failed to save vault.");
      console.error(err);
    }
  };

  const handleSelectEnv = (projectName, env) => {
    setSelectedEnvs(prev => ({ ...prev, [projectName]: env }));
  };

  const getSelectedEnv = (projectName) => {
    return selectedEnvs[projectName] || "Production";
  };

  const handleCopyAll = (vault) => {
    if (!vault.credentials || vault.credentials.length === 0) {
      message.info("No credentials to copy.");
      return;
    }
    const envString = vault.credentials
      .map((cred) => `${cred.key}=${cred.value}`)
      .join("\n");
    navigator.clipboard.writeText(envString);
    message.success("All credentials copied to clipboard!");
  };

  const handleDeleteClick = (id) => {
    setVaultToDelete(id);
    setShowDeleteModal(true);
  };

  const handleCloseDeleteModal = () => {
    setShowDeleteModal(false);
    setVaultToDelete(null);
  };

  const handleDeleteConfirm = async () => {
    try {
      await deleteVault(vaultToDelete).unwrap();
      message.success("Vault deleted.");
      setShowDeleteModal(false);
      setVaultToDelete(null);
      refetch();
      
      if (activeProject) {
        const remainingForProject = vaults.filter(v => v.project_name === activeProject && v.id !== vaultToDelete);
        if (remainingForProject.length === 0) {
          setActiveProject(null);
        }
      }
    } catch (err) {
      message.error("Failed to delete vault.");
    }
  };

  const toggleVisibility = (idKey) => {
    setVisibleSecrets((prev) => ({ ...prev, [idKey]: !prev[idKey] }));
  };

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text);
    message.success("Copied to clipboard!");
  };

  const groupedVaults = React.useMemo(() => {
    const groups = {};
    vaults.forEach((vault) => {
      if (!groups[vault.project_name]) {
        groups[vault.project_name] = { Production: null, Staging: null, Local: null };
      }
      groups[vault.project_name][vault.environment] = vault;
    });
    return groups;
  }, [vaults]);

  if (isLoading) return <Container className="p-5 text-center"><h5>Loading Secure Vault...</h5></Container>;

  if (fetchError) {
    return (
      <Container className="p-5 mt-5">
        <Alert variant="danger" className="shadow-sm border-danger">
          <Alert.Heading className="d-flex align-items-center gap-2">
            <FaLock /> Security Alert: Decryption Failed
          </Alert.Heading>
          <p className="mb-2">
            The backend was unable to decrypt your project vault credentials.
            This happens when the <strong>VAULT_MASTER_KEY</strong> in your server's <code>.env</code> file does not match the key that was originally used to encrypt the records.
          </p>
          <hr />
          <p className="mb-0 fw-semibold text-danger" style={{ fontSize: "0.9rem" }}>
            <strong>Server Response:</strong> {fetchError?.data?.message || fetchError?.message || "Unknown Decryption Error"}
          </p>
        </Alert>
      </Container>
    );
  }

  // --- FULL PAGE CREATE/EDIT FORM VIEW ---
  if (isModalOpen) {
    return (
      <Container fluid className="py-4 bg-light min-vh-100 fade-in">
        <Form onSubmit={handleSubmit}>
          {/* Header */}
          <div className="d-flex justify-content-between align-items-center mb-4">
            <div>
              <h2 className="fw-bold text-dark d-flex align-items-center gap-2 mb-1">
                <FaLock className="text-primary" /> {currentVault ? "Edit Secure Vault" : "Create Secure Vault"}
              </h2>
              <p className="text-muted mb-0">
                {currentVault ? `Configure credentials for ${formData.project_name}` : "Configure a new environment vault"}
              </p>
            </div>
            <Button variant="outline-secondary" className="d-flex align-items-center gap-2 px-4 shadow-sm rounded-pill" onClick={handleCloseModal}>
              <FaArrowLeft /> Cancel
            </Button>
          </div>

          <Row className="g-4">
            {/* Form details card */}
            <Col lg={4}>
              <Card className="border-0 shadow-sm rounded-4 p-4 h-100">
                <h5 className="fw-bold text-dark mb-4">Vault Specifications</h5>
                {errorMessage && (
                  <Alert variant="danger" onClose={() => setErrorMessage("")} dismissible>
                    {errorMessage}
                  </Alert>
                )}
                <Form.Group className="mb-3">
                  <Form.Label className="fw-semibold">Project Name</Form.Label>
                  <Form.Control
                    type="text"
                    required
                    placeholder="e.g., Ideal Corporate Website"
                    value={formData.project_name}
                    onChange={(e) => setFormData({ ...formData, project_name: e.target.value })}
                  />
                </Form.Group>
                <Form.Group className="mb-3">
                  <Form.Label className="fw-semibold">Environment</Form.Label>
                  <Form.Select
                    value={formData.environment}
                    onChange={(e) => setFormData({ ...formData, environment: e.target.value })}
                  >
                    <option value="Production">Production</option>
                    <option value="Staging">Staging</option>
                    <option value="Local">Local</option>
                  </Form.Select>
                </Form.Group>
                <Form.Group className="mb-0">
                  <Form.Label className="fw-semibold">Setup Notes (Optional)</Form.Label>
                  <Form.Control
                    as="textarea"
                    rows={4}
                    placeholder="e.g., Requires VPN to access DB..."
                    value={formData.notes}
                    onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                  />
                </Form.Group>
              </Card>
            </Col>

            {/* Credentials editor card */}
            <Col lg={8}>
              <Card className="border-0 shadow-sm rounded-4 p-4 h-100">
                <div className="d-flex justify-content-between align-items-center mb-4 flex-wrap gap-2">
                  <h5 className="fw-bold text-dark mb-0">Credentials Variables</h5>
                  <div className="d-flex gap-2">
                    <Button 
                      variant={editorMode === "table" ? "primary" : "outline-primary"} 
                      size="sm" 
                      className="px-3 rounded-pill"
                      onClick={() => setEditorMode("table")}
                    >
                      Table Editor
                    </Button>
                    <Button 
                      variant={editorMode === "bulk" ? "primary" : "outline-primary"} 
                      size="sm" 
                      className="px-3 rounded-pill"
                      onClick={() => setEditorMode("bulk")}
                    >
                      Bulk Paste (.env)
                    </Button>
                  </div>
                </div>

                {editorMode === "table" ? (
                  <>
                    <div className="d-flex justify-content-between align-items-center mb-3">
                      <span className="text-muted small">Configure credentials row by row</span>
                      <Button variant="outline-primary" size="sm" onClick={handleAddCredential} className="d-flex align-items-center gap-1 rounded-pill px-3">
                        <MdAdd /> Add Row
                      </Button>
                    </div>
                    <div className="bg-light border rounded p-3 overflow-auto" style={{ maxHeight: '420px' }}>
                      {formData.credentials.map((cred, idx) => (
                        <Row key={idx} className="align-items-center bg-white p-3 border rounded shadow-sm mb-3 mx-0">
                          <Col sm={12} md={4} className="mb-2 mb-md-0">
                            <Form.Control
                              type="text"
                              required
                              placeholder="Key (e.g., DB_HOST)"
                              value={cred.key}
                              onChange={(e) => handleCredentialChange(idx, "key", e.target.value)}
                              style={{ fontFamily: 'monospace' }}
                            />
                          </Col>
                          <Col sm={12} md={5} className="mb-2 mb-md-0">
                            <InputGroup>
                              <Form.Control
                                type={cred.is_secret && cred.value && currentVault ? "password" : "text"}
                                required
                                placeholder="Value"
                                value={cred.value}
                                onChange={(e) => handleCredentialChange(idx, "value", e.target.value)}
                                style={{ fontFamily: 'monospace' }}
                              />
                              {currentVault && cred.is_secret && cred.value && (
                                <InputGroup.Text className="text-muted" style={{ fontSize: '0.75rem' }}>(Stored)</InputGroup.Text>
                              )}
                            </InputGroup>
                          </Col>
                          <Col sm={12} md={3} className="d-flex justify-content-between align-items-center">
                            <Form.Check
                              type="switch"
                              label="Secret"
                              checked={cred.is_secret}
                              onChange={(e) => handleCredentialChange(idx, "is_secret", e.target.checked)}
                              className="text-muted fw-semibold"
                            />
                            <Button variant="outline-danger" size="sm" onClick={() => handleRemoveCredentialClick(idx)} className="border-0 bg-transparent text-danger">
                              <MdDelete size={20} />
                            </Button>
                          </Col>
                        </Row>
                      ))}
                      {formData.credentials.length === 0 && (
                        <div className="text-center text-muted py-5">No credentials added yet. Click "Add Row" or use "Bulk Paste".</div>
                      )}
                    </div>
                  </>
                ) : (
                  <div className="d-flex flex-column h-100">
                    <div className="d-flex justify-content-between align-items-center mb-3">
                      <span className="text-muted small">Paste variables in KEY=VALUE format. One per line.</span>
                    </div>
                    <Form.Control
                      as="textarea"
                      rows={14}
                      value={bulkText}
                      onChange={(e) => handleBulkTextChange(e.target.value)}
                      placeholder={`# Paste .env contents here:\nDB_HOST=127.0.0.1\nDB_PORT=5432\nDB_PASSWORD="your_password_here"`}
                      className="font-monospace p-3 bg-dark text-white rounded-3 border-0 flex-grow-1"
                      style={{ fontSize: "0.9rem", resize: "none" }}
                    />
                    <div className="text-muted small mt-2">
                      Successfully parsed {formData.credentials.length} variables. Empty lines and lines starting with '#' are ignored.
                    </div>
                  </div>
                )}

                <div className="d-flex justify-content-end gap-3 mt-4 border-top pt-3">
                  <Button variant="light" className="px-4 rounded-pill" onClick={handleCloseModal}>
                    Cancel
                  </Button>
                  <Button variant="primary" type="submit" disabled={isAdding || isUpdating} className="px-4 d-flex align-items-center gap-2 rounded-pill">
                    {(isAdding || isUpdating) ? "Saving..." : "Save Securely"} <FaLock size={14} />
                  </Button>
                </div>
              </Card>
            </Col>
          </Row>
        </Form>

        {/* Delete Confirmation Modal for Rows */}
        <DeleteModel
          DELETESTATE={showRowDeleteModal}
          ONCLICK={closeRowDeleteModal}
          YES={confirmRemoveCredential}
          DELETETITLE="Delete Credential"
          DESCRIPTION="Are you sure you want to remove this credential row? You must click 'Save Securely' to apply the change."
          loadTest="Removing..."
        />
      </Container>
    );
  }

  // --- LIST VIEW (Default Projects Grid & Active Project Details) ---
  return (
    <Container fluid className="py-4 bg-light min-vh-100">
      {/* Header */}
      <div className="d-flex justify-content-between align-items-center mb-4">
        <div>
          <h2 className="fw-bold text-dark d-flex align-items-center gap-2 mb-1">
            <FaLock className="text-primary" /> Project Credentials Vault
          </h2>
          <p className="text-muted mb-0">
            Securely store and reference your environment variables, VPS credentials, and API keys.
          </p>
        </div>
        {!activeProject && (
          <Button variant="primary" className="d-flex align-items-center gap-2 px-4 shadow-sm rounded-pill" onClick={() => handleOpenModal()}>
            <MdAdd size={20} /> New Vault
          </Button>
        )}
      </div>

      {!activeProject ? (
        // --- MAIN PAGE (Grid of Projects) ---
        <Row className="g-4">
          {Object.entries(groupedVaults).map(([projectName, envMap]) => {
            const configuredCount = Object.values(envMap).filter(v => v !== null).length;
            return (
              <Col md={4} xl={3} key={projectName}>
                <Card 
                  className="h-100 shadow-sm border-0 text-center rounded-4 stat-card" 
                  onClick={() => setActiveProject(projectName)}
                  style={{ cursor: "pointer", transition: "transform 0.2s" }}
                >
                  <Card.Body className="p-4">
                    <div className="bg-primary bg-opacity-10 text-primary rounded-circle d-inline-flex p-3 mb-3">
                      <FcPrivacy size={24} />
                    </div>
                    <h5 className="fw-bold text-dark mb-1">{projectName}</h5>
                    <p className="text-muted small mb-0">
                      {configuredCount} Environment{configuredCount !== 1 ? 's' : ''} Configured
                    </p>
                  </Card.Body>
                </Card>
              </Col>
            );
          })}

          {Object.keys(groupedVaults).length === 0 && (
            <Col xs={12}>
              <div className="text-center py-5 bg-white border border-dashed rounded-4">
                <FaLock size={40} className="text-muted mb-3 opacity-50" />
                <h5 className="text-secondary fw-bold">Your vault is empty.</h5>
                <p className="text-muted mb-0">Click "New Vault" to store your first project's credentials.</p>
              </div>
            </Col>
          )}
        </Row>
      ) : (
        // --- INNER PAGE (Project Details) ---
        <div className="fade-in">
          <Button 
            variant="link" 
            className="text-decoration-none text-muted p-0 mb-3 d-flex align-items-center gap-2" 
            onClick={() => setActiveProject(null)}
          >
            <FaArrowLeft /> Back to Projects
          </Button>
          
          <Row>
            <Col sm={12}>
              {(() => {
                const projectName = activeProject;
                const envMap = groupedVaults[projectName] || { Production: null, Staging: null, Local: null };
                const activeEnv = getSelectedEnv(projectName);
                const activeVault = envMap[activeEnv];

                return (
              <Card className="shadow-sm border-0 rounded-4">
                <Card.Header className="bg-white d-flex justify-content-between align-items-center py-3 border-bottom rounded-top-4">
                  <div className="d-flex align-items-center flex-wrap gap-2">
                    <h5 className="mb-0 fw-bold text-dark">{projectName}</h5>
                    <Form.Select 
                      size="sm" 
                      className="d-inline-block w-auto border-0 bg-light text-muted fw-semibold rounded-pill px-3"
                      value={activeEnv}
                      onChange={(e) => handleSelectEnv(projectName, e.target.value)}
                    >
                      <option value="Production">🟢 Production</option>
                      <option value="Staging">🟠 Staging</option>
                      <option value="Local">🔵 Local</option>
                    </Form.Select>
                  </div>
                  <div className="d-flex gap-2">
                    <Button 
                      variant="outline-secondary" 
                      size="sm" 
                      className="rounded-circle d-flex align-items-center justify-content-center"
                      style={{ width: "32px", height: "32px" }}
                      onClick={() => activeVault ? handleCopyAll(activeVault) : message.info(`No ${activeEnv} credentials to copy.`)} 
                      title={`Copy All ${activeEnv} .env`}
                    >
                      <MdContentCopy size={16} />
                    </Button>
                    <Button 
                      variant="outline-primary" 
                      size="sm" 
                      className="rounded-circle d-flex align-items-center justify-content-center"
                      style={{ width: "32px", height: "32px" }}
                      onClick={() => activeVault ? handleOpenModal(activeVault) : message.info(`Please create a ${activeEnv} vault first.`)} 
                      title={`Edit ${activeEnv} Vault`}
                    >
                      <MdEdit size={16} />
                    </Button>
                    <Button 
                      variant="outline-danger" 
                      size="sm" 
                      className="rounded-circle d-flex align-items-center justify-content-center"
                      style={{ width: "32px", height: "32px" }}
                      onClick={() => activeVault ? handleDeleteClick(activeVault.id) : message.info(`No ${activeEnv} vault exists to delete.`)} 
                      title={`Delete ${activeEnv} Vault`}
                    >
                      <MdDelete size={16} />
                    </Button>
                  </div>
                </Card.Header>

                <Card.Body className="p-4">
                  {activeVault ? (
                    <>
                      {activeVault.notes && (
                        <div className="bg-warning bg-opacity-10 text-dark p-3 rounded-3 mb-4 border border-warning">
                          <strong>Notes:</strong> {activeVault.notes}
                        </div>
                      )}

                      <div className="d-flex flex-column gap-3">
                        {activeVault.credentials?.map((cred, idx) => {
                          const isVisible = visibleSecrets[`${activeVault.id}_${idx}`];
                          const displayValue = !cred.is_secret || isVisible ? cred.value : "••••••••••••••••";

                          return (
                            <div key={idx} className="d-flex flex-column flex-sm-row justify-content-between align-items-sm-center bg-light p-2 rounded-3 border">
                              <div className="fw-semibold text-secondary mb-2 mb-sm-0 text-break ps-2" style={{ fontFamily: "monospace", fontSize: "0.9rem" }}>
                                {cred.key}
                              </div>
                              <div className="d-flex align-items-center bg-white border rounded px-2 py-1 flex-grow-1 ms-sm-3" style={{ minWidth: 0 }}>
                                <span className="text-truncate flex-grow-1 text-muted font-monospace" style={{ fontSize: "0.85rem" }}>
                                  {displayValue}
                                </span>
                                <div className="d-flex gap-1 ms-2">
                                  {cred.is_secret && (
                                    <Button variant="light" size="sm" className="p-1 text-secondary" onClick={() => toggleVisibility(`${activeVault.id}_${idx}`)}>
                                      {isVisible ? <MdVisibilityOff size={16} /> : <MdVisibility size={16} />}
                                    </Button>
                                  )}
                                  <Button variant="light" size="sm" className="p-1 text-secondary" onClick={() => copyToClipboard(cred.value)}>
                                    <MdContentCopy size={16} />
                                  </Button>
                                </div>
                              </div>
                            </div>
                          );
                        })}
                        {(!activeVault.credentials || activeVault.credentials.length === 0) && (
                          <p className="text-muted text-center my-3 small">No credentials saved yet.</p>
                        )}
                      </div>
                    </>
                  ) : (
                    <div className="text-center py-5">
                      <FaCodeBranch size={30} className="text-muted mb-2 opacity-50" />
                      <h6 className="text-secondary fw-semibold">No {activeEnv} Vault</h6>
                      <p className="text-muted small mb-3">Add credentials for the {activeEnv} environment.</p>
                      <Button variant="primary" size="sm" className="px-3 rounded-pill" onClick={() => handleOpenModalForNewEnv(projectName, activeEnv)}>
                        <MdAdd /> Create {activeEnv} Vault
                      </Button>
                    </div>
                  )}
                </Card.Body>
              </Card>
              );
              })()}
            </Col>
          </Row>
        </div>
      )}

      {/* Delete Confirmation Modal */}
      <DeleteModel
        DELETESTATE={showDeleteModal}
        ONCLICK={handleCloseDeleteModal}
        YES={handleDeleteConfirm}
        DELETETITLE="Delete Vault"
        DESCRIPTION="Are you sure you want to delete this project vault entirely? This action cannot be undone."
        loadTest="Deleting..."
      />

      <style>{`
        .stat-card {
          transition: transform 0.2s ease, box-shadow 0.2s ease;
        }
        .stat-card:hover {
          transform: translateY(-5px);
          box-shadow: 0 10px 20px rgba(0,0,0,0.08) !important;
        }
      `}</style>
    </Container>
  );
};

export default ProjectVault;
